import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { 
  Users, MessageSquare, Activity, Globe, Clock, TrendingUp, 
  Eye, Database, Shield, RefreshCw 
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { AnalyticsData } from '../types';
import { getDashboardMetrics } from '../services/analyticsApi';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const AnalyticsDashboard: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  useEffect(() => {
    loadAnalytics();
    // Rafraîchir toutes les 5 minutes
    const interval = setInterval(loadAnalytics, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await getDashboardMetrics();
      setAnalytics(data);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'dd MMM', { locale: fr });
    } catch {
      return dateString;
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    change?: string;
    color: string;
  }> = ({ title, value, icon, change, color }) => (
    <div className="bg-white rounded-xl shadow-lg p-6 border-l-4" style={{ borderLeftColor: color }}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
          {change && (
            <p className="text-sm text-green-600 mt-1">
              <TrendingUp className="inline w-4 h-4 mr-1" />
              {change}
            </p>
          )}
        </div>
        <div 
          className="p-3 rounded-full"
          style={{ backgroundColor: `${color}20` }}
        >
          <div style={{ color }}>{icon}</div>
        </div>
      </div>
    </div>
  );

  if (loading && !analytics) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <Activity className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Erreur</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <button
              onClick={loadAnalytics}
              className="mt-2 text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Dernière mise à jour: {format(lastRefresh, 'dd/MM/yyyy à HH:mm', { locale: fr })}
          </p>
        </div>
        <button
          onClick={loadAnalytics}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Visiteurs Uniques"
          value={analytics.uniqueVisitors.toLocaleString()}
          icon={<Users className="w-6 h-6" />}
          color="#0088FE"
        />
        <StatCard
          title="Total Visites"
          value={analytics.totalVisitors.toLocaleString()}
          icon={<Eye className="w-6 h-6" />}
          color="#00C49F"
        />
        <StatCard
          title="Requêtes Messages"
          value={analytics.totalMessageRequests.toLocaleString()}
          icon={<MessageSquare className="w-6 h-6" />}
          color="#FFBB28"
        />
        <StatCard
          title="Messages/Utilisateur"
          value={analytics.averageMessagesPerUser.toFixed(1)}
          icon={<TrendingUp className="w-6 h-6" />}
          color="#FF8042"
        />
      </div>

      {/* Content Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Total Messages"
          value={analytics.totalMessages?.toLocaleString() || '0'}
          icon={<Database className="w-6 h-6" />}
          color="#8884D8"
        />
        <StatCard
          title="Total Noms"
          value={analytics.totalNames?.toLocaleString() || '0'}
          icon={<Users className="w-6 h-6" />}
          color="#82CA9D"
        />
        <StatCard
          title="Actions Admin"
          value={analytics.adminActivity.totalActions.toLocaleString()}
          icon={<Shield className="w-6 h-6" />}
          color="#FFC658"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Visitors by Day */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Visiteurs par Jour (30 derniers jours)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={analytics.visitorsByDay}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                fontSize={12}
              />
              <YAxis fontSize={12} />
              <Tooltip 
                labelFormatter={(label) => formatDate(label)}
                formatter={(value, name) => [
                  value,
                  name === 'unique_visitors' ? 'Visiteurs uniques' : 'Vues totales'
                ]}
              />
              <Area
                type="monotone"
                dataKey="unique_visitors"
                stackId="1"
                stroke="#0088FE"
                fill="#0088FE"
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="total_views"
                stackId="2"
                stroke="#00C49F"
                fill="#00C49F"
                fillOpacity={0.6}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Request Types */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Types de Requêtes</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analytics.messageRequestsByType}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ request_type, percent }) => 
                  `${request_type} (${(percent * 100).toFixed(0)}%)`
                }
                outerRadius={80}
                fill="#8884d8"
                dataKey="count"
              >
                {analytics.messageRequestsByType.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Pages les Plus Visitées</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics.topPages}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="page" 
                fontSize={12}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis fontSize={12} />
              <Tooltip />
              <Bar dataKey="views" fill="#0088FE" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top Countries */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Top Pays</h2>
          <div className="space-y-3">
            {analytics.topCountries && analytics.topCountries.length > 0 ? (
              analytics.topCountries.slice(0, 8).map((country, index) => (
                <div key={country.country || index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Globe className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm font-medium">{country.country || 'Inconnu'}</span>
                  </div>
                  <div className="flex items-center">
                    <div 
                      className="bg-blue-200 h-2 rounded mr-2"
                      style={{ 
                        width: `${Math.max(20, (country.count / Math.max(...analytics.topCountries.map(c => c.count))) * 100)}px`
                      }}
                    ></div>
                    <span className="text-sm text-gray-600">{country.count}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Globe className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p>Aucune donnée de géolocalisation disponible</p>
                <p className="text-xs mt-1">Les données de pays s'afficheront automatiquement avec les futurs visiteurs</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Performance & Active Users */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Response Times */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Temps de Réponse</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{analytics.responseTimeStats.min}ms</div>
              <div className="text-sm text-gray-600">Minimum</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{analytics.responseTimeStats.average}ms</div>
              <div className="text-sm text-gray-600">Moyenne</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{analytics.responseTimeStats.max}ms</div>
              <div className="text-sm text-gray-600">Maximum</div>
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-gray-600">
            <Clock className="w-4 h-4 mr-1" />
            Basé sur les requêtes des dernières 24h
          </div>
        </div>

        {/* Active Users */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Utilisateurs Actifs (24h)</h2>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {analytics.activeUsers.map((user, index) => (
              <div key={user.visitor_ip} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                  <span className="text-sm font-mono">{user.visitor_ip}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium">{user.requests} requêtes</div>
                  <div className="text-xs text-gray-500">
                    {format(parseISO(user.last_activity), 'HH:mm', { locale: fr })}
                  </div>
                </div>
              </div>
            ))}
            {analytics.activeUsers.length === 0 && (
              <div className="text-center text-gray-500 py-4">
                Aucun utilisateur actif dans les dernières 24h
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Admin Activity */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Activité Administrateur</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{analytics.adminActivity.creates}</div>
            <div className="text-sm text-gray-600">Créations</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{analytics.adminActivity.updates}</div>
            <div className="text-sm text-gray-600">Modifications</div>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{analytics.adminActivity.deletes}</div>
            <div className="text-sm text-gray-600">Suppressions</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{analytics.adminActivity.totalActions}</div>
            <div className="text-sm text-gray-600">Total Actions</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
